import {Formik, Form, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import {FormActionMap, TransportAdminBase, TransportAdminFormValues} from './type'
import toast from 'react-hot-toast'
import {createTransport, updateTransport} from './api'
import {handleApiError} from '../../../helpers/errorHandler'
import FormikControl from '../../../components/formikControl/FormikControl'
import {getCountryCallingCode, isPossiblePhoneNumber} from 'react-phone-number-input'
import {
  mapAPIDataToFormValues,
  mapFormDataToCreatePayload,
  mapFormDataToUpdatePayload,
} from './mapper'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'

interface TransportAdminFormProps {
  initialValues?: TransportAdminBase
  onAfterSubmit: () => void
}

const formInitialValues: Omit<TransportAdminFormValues, 'id' | 'userId'> = {
  name: '',
  mobile: '',
  countryCode: '',
  status: '1',
}
const validationSchema: Yup.Schema<Omit<TransportAdminFormValues, 'id' | 'userId'>> = Yup.object({
  name: Yup.string()
    .max(100, 'The Transport name cannot exceed 100 characters')
    .required('Transport Name is required'),
  status: Yup.string()
    .oneOf(['0', '1'], 'Transport Status must be either "0" or "1"')
    .required('Transport Status is required'),
  mobile: Yup.string()
    .trim()
    .required('Mobile Number is required')
    .test('mobile', 'Enter a valid phone number', (value) => {
      if (value) {
        return isPossiblePhoneNumber(value)
      }
      return false
    })
    .notOneOf([''], 'Mobile Number is Required'),
  countryCode: Yup.string()
    .trim()
    .required('Country Code is required')
    .notOneOf([''], 'Country Code is Required'),
})

function TransportAdminForm({initialValues, onAfterSubmit}: TransportAdminFormProps) {
  const formType = initialValues ? 'update' : 'add'

  const formActionMap: FormActionMap = {
    add: {
      action: createTransport,
      mapPayload: mapFormDataToCreatePayload,
    },
    update: {
      action: updateTransport,
      mapPayload: mapFormDataToUpdatePayload,
    },
  }

  const submitHandler = async (
    values: Omit<TransportAdminFormValues, 'id' | 'userId'> | TransportAdminFormValues,
    {setSubmitting}: FormikHelpers<any>
  ) => {
    const toastId = toast.loading('Submitting')

    const executeAction = (actionPromise: Promise<any>) => {
      actionPromise
        .then(() => {
          toast.success('Success', {id: toastId})
          onAfterSubmit()
        })
        .catch((err) => {
          handleApiError(err, toastId)
        })
        .finally(() => {
          setSubmitting(false)
        })
    }

    if (formType === 'add') {
      const {action, mapPayload} = formActionMap.add
      const payload = mapPayload(values)
      const promise = action(payload)
      executeAction(promise)
    } else {
      const {action, mapPayload} = formActionMap.update
      const payload = mapPayload(values)
      const promise = action(payload)
      executeAction(promise)
    }
  }

  if (formType === 'update') {
    // if mobile number doesnt includes country code, add it
    if (!initialValues?.mobile.includes('+' + initialValues?.country_code)) {
      initialValues!.mobile = '+' + initialValues!.country_code + initialValues!.mobile
    }
  }

  const formValues = initialValues ? mapAPIDataToFormValues(initialValues) : formInitialValues

  return (
    <div className="px-5 py-3">
      <Formik<Omit<TransportAdminFormValues, 'id' | 'userId'>>
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={submitHandler}
      >
        {({isSubmitting, resetForm, setFieldValue, dirty, isValid}) => (
          <Form>
            <FormikControl
              control="input"
              required
              placeholder="Transport Admin Name"
              label="Transport Admin Name"
              name="name"
            />
            <FormikControl
              control="phone-input"
              required
              placeholder="Mobile Number"
              label="Mobile Number"
              name="mobile"
              onCountryChange={(value: any) => {
                let code
                if (value) {
                  code = getCountryCallingCode(value)
                } else {
                  code = ''
                }
                setFieldValue('countryCode', code)
              }}
            />
            <FormikControl
              control="check"
              type="radio"
              options={[
                {key: 'Active', value: '1'},
                {key: 'Inactive', value: '0'},
              ]}
              required
              placeholder="Active Status"
              label="Active Status"
              name="status"
            />

            <div className="text-center pt-10">
              <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />

              <FormSubmitBtn disabled={!dirty || !isValid || isSubmitting} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default TransportAdminForm
